import styled from "@emotion/styled"
import { PButton, PInlineNotification, PText } from "@porsche-design-system/components-react"
import { spacingStaticMd } from "@porsche-design-system/components-react/emotion"
import type { CreateOfferResult } from "@slfinrtl/ui-bff-routes"
import { createDosOfferRoute, CustomerType, customerTypeMessages } from "@slfinrtl/ui-bff-routes"
import { useCallback, useContext, useState } from "react"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { useNavigate } from "react-router"
import { formMessages } from "src/common/form.messages"
import { commonMessages } from "src/common/messages/common.messages"
import { useDealerAppRoutes } from "src/common/routes/useDealerAppRoutes"
import { ButtonGroup, FormLayout } from "src/components"
import type { RadioOption } from "src/components/form/radio/radioGroup/RadioGroup"
import { RadioGroup } from "src/components/form/radio/radioGroup/RadioGroup"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { Modal } from "src/context/modal/Modal"
import { ModalContext } from "src/context/modal/ModalContext"
import type { ModalProps } from "src/context/modal/ModalProvider"
import { useBffDynamic } from "src/hooks/useBffDynamic/useBffDynamic"

const messages = defineMessages({
	createOfferFailedDescription: {
		defaultMessage: "There was an error creating this offer, please try again.",
		id: "createOffer.modal.createOfferFailedText",
	},
	createOfferFailedHeader: {
		defaultMessage: "Offer could not be created.",
		id: "createOffer.modal.createOfferFailedHeader",
	},
	selectionHint: {
		defaultMessage: "Please select a customer type",
		id: "createOffer.modal.selectionHint",
	},
})

export interface CreateOfferModalContainerProps {
	vehicleKey: string
}

type Props = ModalProps<CreateOfferModalContainerProps>

export const CreateOfferModalContainer = withErrorHandler(({ id, modalProps }: Props) => {
	const { hideModal } = useContext(ModalContext)
	const [selectedCustomerType, setSelectedCustomerType] = useState<CustomerType>(
		CustomerType.END_CUSTOMER as CustomerType,
	)
	const [isLoading, setIsLoading] = useState(false)
	const [isRetrying, setIsRetrying] = useState(false)
	const [hasError, setHasError] = useState<boolean | undefined>()

	const { formatMessage } = useIntl()
	const routes = useDealerAppRoutes()

	const createEmptyOffer = useBffDynamic(createDosOfferRoute, "inline")

	function executeCreateEmptyOffer() {
		createEmptyOffer({
			body: {
				salesChannel: selectedCustomerType,
				vehicleId: modalProps.vehicleKey,
			},
			pathParams: {},
			queryParams: {},
		})
			.then((data: CreateOfferResult) => {
				if (routes?.inventoryList?.vehicleDetails?.editOffer) {
					navigate(routes.inventoryList.vehicleDetails.editOffer.url(modalProps.vehicleKey, data.offerId))
				}
				hideModal(id)
			})
			.catch(onError)
	}

	const options: RadioOption[] = [
		{
			label: formatMessage(customerTypeMessages.END_CUSTOMER),
			value: CustomerType.END_CUSTOMER,
		},
		{
			label: formatMessage(customerTypeMessages.RESELLER),
			value: CustomerType.RESELLER,
		},
	]

	const hide = () => hideModal(id)
	const navigate = useNavigate()

	const onSubmit = () => {
		setIsLoading(true)
		setHasError(undefined)
		executeCreateEmptyOffer()
	}
	const onError = () => {
		setIsLoading(false)
		setIsRetrying(false)
		setHasError(true)
	}
	const onRetry = () => {
		setIsRetrying(true)
		executeCreateEmptyOffer()
	}

	const handleCustomerTypeChange = useCallback((val: string) => setSelectedCustomerType(val as CustomerType), [])

	return (
		<Modal id={id} heading={formatMessage(commonMessages.customerType)}>
			<StyledErrorNotificationInline />
			<FormLayout>
				<PText>
					<FormattedMessage {...messages.selectionHint} />
				</PText>
				<RadioGroup
					name="customerTypeSelection"
					value={selectedCustomerType}
					onChange={handleCustomerTypeChange}
					required
					options={options}
				/>

				{hasError && (
					<StyledInlineNotification
						heading={formatMessage(messages.createOfferFailedHeader)}
						description={formatMessage(messages.createOfferFailedDescription)}
						state="error"
						dismissButton={false}
						actionLabel={formatMessage(commonMessages.retry)}
						actionIcon="refresh"
						actionLoading={isRetrying}
						onAction={onRetry}
					/>
				)}
			</FormLayout>

			<ButtonGroup>
				<PButton loading={isLoading} disabled={hasError} onClick={onSubmit}>
					<FormattedMessage {...commonMessages.proceed} />
				</PButton>
				<PButton type="button" variant="secondary" onClick={hide}>
					<FormattedMessage {...formMessages.cancel} />
				</PButton>
			</ButtonGroup>
		</Modal>
	)
})

const StyledErrorNotificationInline = styled(ErrorNotificationInline)`
	margin-top: 0;
`

const StyledInlineNotification = styled(PInlineNotification)`
	margin-top: ${spacingStaticMd};
`
